import React, { useEffect, useState, createContext, useContext, type ReactNode } from 'react';
import { Framework, CategoryType, frameworks as initialFrameworks, categories as initialCategories, categoryColors as initialCategoryColors } from '../data/frameworks';
interface FrameworksContextType {
  frameworks: Framework[];
  categories: string[];
  categoryColors: Record<string, string>;
  addFramework: (framework: Framework) => void;
  updateFramework: (id: string, framework: Framework) => void;
  deleteFramework: (id: string) => void;
  addCategory: (category: string) => void;
  updateCategory: (oldName: string, newName: string) => void;
  deleteCategory: (category: string) => void;
}
const FrameworksContext = createContext<FrameworksContextType | undefined>(undefined);
const COLOR_PALETTE = ['bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800', 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800', 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800', 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800', 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800', 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 border-fuchsia-200 dark:border-fuchsia-800', 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300 border-lime-200 dark:border-lime-800', 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800', 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 border-teal-200 dark:border-teal-800'];
export function FrameworksProvider({
  children
}: {
  children: ReactNode;
}) {
  const [frameworks, setFrameworks] = useState<Framework[]>(() => {
    const saved = localStorage.getItem('sf_frameworks');
    return saved ? JSON.parse(saved) : initialFrameworks;
  });
  const [categories, setCategories] = useState<string[]>(() => {
    const saved = localStorage.getItem('sf_categories');
    return saved ? JSON.parse(saved) : initialCategories;
  });
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>(() => {
    const saved = localStorage.getItem('sf_categoryColors');
    return saved ? JSON.parse(saved) : initialCategoryColors;
  });
  useEffect(() => {
    localStorage.setItem('sf_frameworks', JSON.stringify(frameworks));
  }, [frameworks]);
  useEffect(() => {
    localStorage.setItem('sf_categories', JSON.stringify(categories));
  }, [categories]);
  useEffect(() => {
    localStorage.setItem('sf_categoryColors', JSON.stringify(categoryColors));
  }, [categoryColors]);
  const addFramework = (framework: Framework) => {
    setFrameworks(prev => [...prev, framework]);
  };
  const updateFramework = (id: string, updatedFramework: Framework) => {
    setFrameworks(prev => prev.map(f => f.id === id ? updatedFramework : f));
  };
  const deleteFramework = (id: string) => {
    setFrameworks(prev => prev.filter(f => f.id !== id));
  };
  const addCategory = (category: string) => {
    if (categories.includes(category)) return;
    setCategories(prev => [...prev, category]);
    // Assign a color from palette
    const colorIndex = categories.length % COLOR_PALETTE.length;
    setCategoryColors(prev => ({
      ...prev,
      [category]: COLOR_PALETTE[colorIndex]
    }));
  };
  const updateCategory = (oldName: string, newName: string) => {
    if (categories.includes(newName)) return;
    setCategories(prev => prev.map(c => c === oldName ? newName : c));
    setCategoryColors(prev => {
      const newColors = {
        ...prev
      };
      newColors[newName] = newColors[oldName];
      delete newColors[oldName];
      return newColors;
    });
    setFrameworks(prev => prev.map(f => f.category === oldName ? {
      ...f,
      category: newName as CategoryType
    } : f));
  };
  const deleteCategory = (category: string) => {
    setCategories(prev => prev.filter(c => c !== category));
    setCategoryColors(prev => {
      const newColors = {
        ...prev
      };
      delete newColors[category];
      return newColors;
    });
    // Frameworks in this category will need to be handled by the UI (e.g. warning user)
  };
  return <FrameworksContext.Provider value={{
    frameworks,
    categories,
    categoryColors,
    addFramework,
    updateFramework,
    deleteFramework,
    addCategory,
    updateCategory,
    deleteCategory
  }} data-id="element-263">
      {children}
    </FrameworksContext.Provider>;
}
export function useFrameworks() {
  const context = useContext(FrameworksContext);
  if (context === undefined) {
    throw new Error('useFrameworks must be used within a FrameworksProvider');
  }
  return context;
}